import React, { useState, useMemo, useCallback } from 'react'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { useMutation } from '@sequencehq/api/utils'
import {
  IndigoIndigo60,
  Primary14ExtendedRegular,
  Box,
  Flex,
  Link
} from '@sequencehq/core-components'
import { isPositiveInteger, required, skip } from '@sequencehq/utils/validation'
import { useNotifications } from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import {
  DatePickerField,
  SimpleModal,
  TextInputField,
  Checkbox
} from '@sequencehq/core-components'
import { CustomerAliasSelect, CustomerSelect } from 'Customer/index'
import { useLoadAllItemsQuery } from '@sequencehq/api/utils'
import { format, HOURS } from '@sequencehq/utils/dates'
import { SeatTypeSelect } from 'Seats/SeatMetric/SeatTypeSelect'

/**
 * Helper function to create customer/alias validation configuration
 * @param withAlias Whether to use customer alias validation or customer ID validation
 * @returns Array of validation configurations for customerAlias and customerId fields
 */
const createCustomerAliasValidation = (withAlias: boolean) => [
  {
    property: 'customerAlias' as const,
    validation: [withAlias ? required : skip]
  },
  { property: 'customerId' as const, validation: [withAlias ? skip : required] }
]

interface CreateSeatSnapshotDrawerProps {
  trigger: React.ReactElement
  onSuccess?: (
    seatType: DashboardApi20240730.PostSeatEvent.Response['data']
  ) => void
}

type CustomerAliasFields = {
  customerAlias: string
  customerId: string
}

type FormValues = CustomerAliasFields & {
  seatType: string
  snapshotTimestamp: string
  total: string
}

export const CreateSeatSnapshotDrawer = ({
  trigger,
  onSuccess
}: CreateSeatSnapshotDrawerProps) => {
  const { displayNotification } = useNotifications()
  const [withAlias, setWithAlias] = useState(false)
  const aliasesInfiniteQuery = useLoadAllItemsQuery(
    dashboard20240730Client.getCustomerAliases,
    { limit: 100 },
    { staleTime: 1 * HOURS }
  )

  const aliases = useMemo(
    () => aliasesInfiniteQuery.data?.pages.flatMap(page => page.items),
    [aliasesInfiniteQuery.data]
  )

  const createSeatEventMutation = useMutation(
    dashboard20240730Client.postSeatEvent
  )

  const fieldConfiguration = useMemo(
    () => [
      ...createCustomerAliasValidation(withAlias),
      { property: 'seatType' as const, validation: [required] },
      { property: 'snapshotTimestamp' as const, validation: [required] },
      { property: 'total' as const, validation: [isPositiveInteger] }
    ],
    [withAlias]
  )

  const form = useForm<FormValues>({
    value: {
      customerAlias: '',
      customerId: '',
      seatType: '',
      snapshotTimestamp: '',
      total: ''
    },
    fieldConfiguration
  })

  const handleAliasToggle = useCallback(
    (checked: boolean) => {
      setWithAlias(checked)
      // Clear the field that's no longer being used
      if (checked) {
        form.fields.customerId.onChange('')
      } else {
        form.fields.customerAlias.onChange('')
      }
    },
    [form.fields.customerId, form.fields.customerAlias]
  )

  /**
   * Converts the form data to the expected customer alias format for the API.
   * When using aliases: converts alias ID (from CustomerAliasSelect) to alias value (expected by API)
   * When using customer ID: returns the customer ID directly
   * @returns The customer alias value or customer ID to send to the API
   */
  const getCustomerAlias = useCallback((): string => {
    if (withAlias) {
      const aliasId = form.fields.customerAlias.value as string
      const aliasObject = aliases?.find(alias => alias.id === aliasId)
      return aliasObject?.value || aliasId // fallback to ID if not found
    } else {
      return form.fields.customerId.value as string
    }
  }, [withAlias, form.fields, aliases])

  const handleSubmit = async () => {
    // Validate required fields before submission
    if (!form.queries.formData.seatType.trim()) {
      displayNotification('Please select a seat type', { type: 'error' })
      return false
    }

    if (withAlias && !form.fields.customerAlias.value.toString().trim()) {
      displayNotification('Please select a customer alias', { type: 'error' })
      return false
    }

    if (!withAlias && !form.fields.customerId.value.toString().trim()) {
      displayNotification('Please select a customer', { type: 'error' })
      return false
    }

    if (!form.queries.formData.snapshotTimestamp.trim()) {
      displayNotification('Please select an event timestamp', { type: 'error' })
      return false
    }

    try {
      const customerAlias = getCustomerAlias()

      const res = await createSeatEventMutation.mutateAsync({
        customerAlias,
        seatType: form.queries.formData.seatType,
        eventTimestamp: `${form.fields.snapshotTimestamp.value}T00:00:00.000Z`,
        total: Number(form.fields.total.value as string)
      })
      displayNotification('Seat event created', { type: 'success' })
      onSuccess?.(res)
      form.mutators.resetFormData()
      return true
    } catch {
      displayNotification("Couldn't create seat event", { type: 'error' })
      return false
    }
  }

  return (
    <SimpleModal
      data-testid="seatEvents.new.drawer"
      trigger={trigger}
      variant="drawer"
      title="Create seat event"
      cancelButtonText="Cancel"
      submitButtonText="Create seat event"
      submittingButtonText="Creating seat event..."
      onSubmit={handleSubmit}
      onClose={form.mutators.resetFormData}
    >
      <Flex direction="column" gap="16px" data-testid="seatEvents.new.form">
        <Box {...Primary14ExtendedRegular} color="inherit">
          Use the 'Seat events' API endpoint to programmatically submit seat
          events to update the balance for a customer.{' '}
          <Link
            to="https://docs.sequencehq.com/seats/seat-updates"
            isExternal
            color={IndigoIndigo60}
          >
            Learn more
          </Link>
        </Box>
        <CustomerSelect
          data-testid="seatEvents.new.customerSelect"
          disabled={withAlias}
          label="Customer"
          onChange={value => form.fields.customerId.onChange(value)}
          value={form.fields.customerId.value as string}
          allowCreate={false}
          validationErrors={form.fields.customerId.validationErrors}
        />
        <Checkbox
          data-testid="seatEvents.new.useCustomerAlias"
          label="Use customer alias instead"
          isChecked={withAlias}
          onChange={event => handleAliasToggle(event.target.checked)}
        />
        {withAlias && (
          <CustomerAliasSelect
            data-testid="seatEvents.new.customerAliasSelect"
            label="Customer alias"
            onChange={value => form.fields.customerAlias.onChange(value)}
            value={form.fields.customerAlias.value as string}
            validationErrors={form.fields.customerAlias.validationErrors}
          />
        )}
        <DatePickerField
          data-testid="seatEvents.new.datePicker"
          width="100%"
          label="Event timestamp"
          placeholder="Select date"
          value={
            form.fields.snapshotTimestamp.value
              ? new Date(form.fields.snapshotTimestamp.value)
              : undefined
          }
          onChange={value =>
            form.fields.snapshotTimestamp.onChange(
              value ? format(value, 'yyyy-MM-dd') : ''
            )
          }
        />
        <SeatTypeSelect
          data-testid="seatEvents.new.seatMetricSelect"
          label="Seat type"
          onChange={value => form.fields.seatType.onChange(value)}
          value={form.fields.seatType.value as string}
          validationErrors={form.fields.seatType.validationErrors}
        />
        <TextInputField
          data-testid="seatEvents.new.totalSeats"
          type="number"
          label="Total seats"
          placeholder="0"
          value={form.fields.total.value}
          onChange={e => form.fields.total.onChange(e.target.value)}
          validationErrors={form.fields.total.validationErrors}
        />
      </Flex>
    </SimpleModal>
  )
}
